// Zephyr documentation content (source: Zephyr_Complete_Documentation.pdf).
// Each page is a list of blocks rendered by the Docs page:
//   { type: "p", text }                     paragraph
//   { type: "h2", text }                    section heading (also listed in "On this page")
//   { type: "h3", text }                    sub heading
//   { type: "list", items: [] }             bullet list
//   { type: "table", head: [], rows: [[]] } table ("Available" / "Coming Soon" cells render as pills)
//   { type: "flow", steps: [{ title, text }], caption, vertical }  flow diagram
//   { type: "note", text }                  highlighted callout

export const docsSections = [
  {
    group: "Getting Started",
    pages: [
      {
        slug: "reading-guide",
        title: "Reading Guide",
        subtitle: "How to navigate the Zephyr technical documentation",
        hero: true,
        blocks: [
          {
            type: "note",
            text: "Privacy. Security. Control. A Next Generation Web3 Browser and Autonomous Intelligence Ecosystem, built on Solana.",
          },
          {
            type: "p",
            text: "This document explains the purpose, design direction, and intended capabilities of Zephyr, a privacy focused Web3 browser developed for the Solana ecosystem. It is written for users, developers, ecosystem participants, and readers evaluating the platform's technical approach.",
          },
          { type: "h2", text: "Document structure" },
          {
            type: "p",
            text: "Chapters 1 and 2 introduce Zephyr and the privacy challenges it aims to address. Chapters 3 through 9 describe the browser infrastructure and proposed privacy components. Chapter 10 presents the complete Zephyr AI Agent concept. Chapters 11 and 12 examine security considerations, privacy boundaries, and user control. Chapters 13 through 15 cover the token ecosystem, developer integrations, and the broader vision.",
          },
          { type: "h2", text: "Feature availability" },
          {
            type: "p",
            text: "Available identifies functionality included in the current browser experience. Coming Soon identifies planned capabilities that are not yet available. Descriptions of planned features explain design objectives rather than confirmed production behavior. The document does not establish a release sequence or delivery date.",
          },
          { type: "h2", text: "Privacy terminology" },
          {
            type: "p",
            text: "Browser data cleanup, wallet separation, transaction privacy, and blockchain anonymity are different concepts. Auto Burn Sessions address temporary browsing information within their supported scope. They do not delete public Solana records. Ephemeral Wallets, mixing mechanisms, and other proposed protections should not be interpreted as guarantees of anonymity or complete resistance to analysis.",
          },
          { type: "h2", text: "AI Agent and user authorization" },
          {
            type: "p",
            text: "The AI Agent chapter describes intended browser automation, creative production, project management, and supported onchain interactions. Actions involving wallet signatures, asset movement, token deployment, or connected accounts require appropriate user permissions and authorization. Detailed agent architecture, fees, and revenue arrangements remain to be specified.",
          },
          { type: "h2", text: "Diagrams and technical illustrations" },
          {
            type: "p",
            text: "Architecture diagrams and workflow illustrations in subsequent chapters are conceptual unless explicitly identified as implemented. Their purpose is to explain component relationships and intended user flows, not to certify deployed security controls or finalized engineering specifications.",
          },
          { type: "h2", text: "Interpretation of this document" },
          {
            type: "p",
            text: "This documentation describes the platform's current direction and planned ecosystem. Technical mechanisms may evolve during implementation and testing. Where a mechanism has not been finalized, the text identifies it as a design objective or Coming Soon capability rather than an established fact.",
          },
        ],
      },
      {
        slug: "introduction",
        chapter: "01",
        title: "Introduction to Zephyr",
        blocks: [
          { type: "h2", text: "1.1 Overview" },
          {
            type: "p",
            text: "Zephyr is a privacy focused, ad free Web3 browser designed for the Solana ecosystem. It brings browser privacy, decentralized application access, and user controlled interactions into a single environment. Its objective is to reduce unnecessary exposure during everyday Web3 activity while keeping the experience accessible to people who use wallets, tokens, and decentralized services.",
          },
          {
            type: "p",
            text: "A Web3 browser handles more than website navigation. It sits at the point where websites request wallet connections, applications ask for permissions, and users approve operations that can affect digital assets. Zephyr treats this environment as an important part of the privacy and security model rather than a neutral window into blockchain applications.",
          },
          { type: "h2", text: "1.2 Design Principles" },
          { type: "h3", text: "Privacy by design" },
          {
            type: "p",
            text: "Zephyr aims to limit unnecessary retention of temporary browsing information and provide clearer separation between different kinds of digital activity.",
          },
          { type: "h3", text: "Isolation and control" },
          {
            type: "p",
            text: "The platform is designed to give users more deliberate control over sessions, application interactions, wallet access, and authorized actions.",
          },
          { type: "h3", text: "Practical usability" },
          {
            type: "p",
            text: "Privacy features should work together within the browser so users do not need to coordinate numerous unrelated tools for routine activities.",
          },
          { type: "h2", text: "1.3 The Browser Foundation" },
          {
            type: "p",
            text: "Auto Burn Sessions are the initial privacy capability. They are designed to clear supported temporary session information when a browsing session ends, reducing the amount of residual local activity data left behind. The exact scope of cleanup depends on the browser implementation and does not extend to records held by external services or public blockchains.",
          },
          {
            type: "p",
            text: "The browsing experience is also designed to be ad free. This reduces advertising interruptions within the product, although it should not be interpreted as a guarantee that third party websites cannot collect information through their own services.",
          },
          { type: "h2", text: "1.4 The Broader Zephyr System" },
          {
            type: "p",
            text: "Zephyr is designed as a modular environment. Its planned components address different sources of exposure and complexity, rather than promising a single mechanism that makes all activity private.",
          },
          {
            type: "table",
            head: ["Component", "Status", "Purpose"],
            rows: [
              ["Browser and Auto Burn Sessions", "Available", "Ad free browsing and supported temporary session cleanup"],
              ["Ephemeral Wallets", "Available", "Additional wallet identities intended to reduce repeated address reuse"],
              ["Mixer and Transaction Privacy", "Coming Soon", "Mechanisms intended to reduce straightforward transaction linkage"],
              ["Application Isolation", "Coming Soon", "Boundaries intended to separate decentralized application activity"],
              ["Metadata and MSG Masking", "Coming Soon", "Proposed protections for selected metadata and execution details"],
              ["Cross Chain Bridge", "Coming Soon", "Supported asset movement between blockchain networks"],
              ["Zephyr AI Agent", "Coming Soon", "Authorized browser tasks, creative production, project creation and management"],
            ],
          },
          {
            type: "p",
            text: "Figure 1.1. Zephyr capability overview. Status labels distinguish available capabilities from planned systems.",
          },
          { type: "h2", text: "1.5 Solana as the Primary Blockchain Environment" },
          {
            type: "p",
            text: "Solana is the primary blockchain environment for Zephyr wallet connectivity, supported decentralized application interactions, and intended token related operations. The browser is designed to interact with this public network while recognizing that blockchain transactions remain observable onchain.",
          },
          {
            type: "p",
            text: "Browser data cleanup cannot delete a completed Solana transaction. Likewise, using a new wallet address does not by itself prevent an observer from identifying relationships between transactions. Each proposed privacy mechanism addresses a particular exposure surface and must be assessed within its own technical limits.",
          },
          { type: "h2", text: "1.6 Intelligent Browser Operations" },
          {
            type: "p",
            text: "The planned Zephyr AI Agent extends the browser concept beyond navigation. It is intended to interpret natural language instructions, coordinate approved workflows, assist with digital creation, and support authorized onchain interactions. A broader project environment is envisioned in which creators can develop token concepts, websites, visual assets, content campaigns, and persistent project identities.",
          },
          {
            type: "p",
            text: "The AI Agent is not a prerequisite for the core browsing experience. Its proposed capabilities, authorization boundaries, project memory, and economic framework are covered together in Chapter 10. Detailed service fees and agent architecture remain Coming Soon.",
          },
          { type: "h2", text: "1.7 Purpose and Scope" },
          {
            type: "p",
            text: "Zephyr seeks to make Web3 browsing more intentional: less unnecessary session residue, clearer boundaries around applications and wallet activity, and fewer disconnected steps between an idea and an authorized action. This documentation describes the browser foundation, the planned architecture, and the limitations that readers should consider when evaluating privacy claims.",
          },
          {
            type: "p",
            text: "The chapters that follow examine the problems Zephyr is designed to address before describing its components, threat model, AI Agent, and ecosystem.",
          },
        ],
      },
      {
        slug: "the-problem",
        chapter: "02",
        title: "The Problem with Today's Web3 Browsing",
        subtitle:
          "Understanding the privacy gaps between browser activity, wallet identity, decentralized applications, and public blockchain records.",
        blocks: [
          { type: "h2", text: "2.1 A Browser Is More Than a Window to Web3" },
          {
            type: "p",
            text: "In conventional browsing, a website visit may leave behind cookies, local storage, cached resources, permissions, and other records. In Web3, the same browser may also facilitate wallet connections, transaction approvals, decentralized application sessions, and access to onchain services. These activities create overlapping records that can persist beyond the moment of interaction.",
          },
          {
            type: "p",
            text: "The privacy challenge is not limited to any single website or blockchain. Information can accumulate in the browser, within applications, at wallet providers, through network infrastructure, and on public ledgers. Addressing one layer does not automatically protect the others.",
          },
          { type: "h2", text: "2.2 Persistent Browser Data and Session Exposure" },
          {
            type: "p",
            text: "Browser storage supports useful features such as authentication and application preferences. It can also allow information from an earlier session to remain available during a later visit. Cached data, saved permissions, and retained application state can increase the amount of information exposed if a device or browsing profile is accessed by another person.",
          },
          {
            type: "p",
            text: "Private browsing modes can reduce certain forms of local retention, but their exact behavior varies by browser and configuration. They do not make network traffic invisible, prevent every form of tracking, or remove transactions from a blockchain. A privacy focused Web3 environment therefore needs clearly defined session boundaries and understandable cleanup behavior.",
          },
          { type: "h2", text: "2.3 Wallet Address Reuse and Identity Linkage" },
          {
            type: "p",
            text: "A public wallet address is a durable identifier on a transparent ledger. When the same address is used across multiple applications, observers may be able to associate otherwise separate transactions and activities with a common onchain identity. Additional information voluntarily shared with applications or published elsewhere can strengthen those associations.",
          },
          {
            type: "p",
            text: "Creating separate wallets may reduce simple address reuse, but separation alone is not a guarantee of anonymity. Funding patterns, transaction timing, transfers between addresses, and external records can still create links. Privacy mechanisms must be described according to what they actually change and what remains observable.",
          },
          { type: "h2", text: "2.4 Decentralized Application Permissions and Exposure" },
          {
            type: "p",
            text: "Decentralized applications may request wallet access, signatures, transaction approvals, and access to browser resources. Some requests are necessary for a service to function. Others may be broader than a user expects or difficult to evaluate without technical experience.",
          },
          {
            type: "p",
            text: "The risk is not that every application is malicious. The challenge is that permissions, session context, and transaction intent are often spread across different interfaces. Users need a clearer view of which application is requesting an action, what is being authorized, and where sensitive information may be retained.",
          },
          { type: "h2", text: "2.5 Public Transactions and the Limits of Browser Privacy" },
          {
            type: "p",
            text: "Solana transactions are recorded on a public blockchain. Depending on the transaction and application design, observers can inspect addresses, transfers, program interactions, and other published information. Browser cleanup cannot reverse those records.",
          },
          {
            type: "p",
            text: "Similarly, changing browser sessions does not prevent a service from recording activity on its own servers. Network operators and other intermediaries may also observe information outside the browser's control. Any credible privacy architecture must distinguish local data protection, application isolation, wallet separation, and transaction privacy rather than presenting them as interchangeable.",
          },
          { type: "h2", text: "2.6 Fragmented Privacy Tools" },
          {
            type: "p",
            text: "Users seeking stronger privacy may need to combine browser settings, wallet practices, application permissions, and external transaction tools. Each component has separate assumptions, controls, and limitations. Moving between them increases complexity and can make it difficult to understand which protections apply to a particular action.",
          },
          {
            type: "p",
            text: "Zephyr is designed to address this fragmentation through a unified browser environment. Its approach begins with Auto Burn Sessions for temporary session data and is intended to expand through additional privacy and interaction modules as those capabilities become available.",
          },
          { type: "h2", text: "2.7 Privacy Exposure Across the Interaction Lifecycle" },
          {
            type: "p",
            text: "The following conceptual diagram shows how information can be generated at different stages of a Web3 interaction. It is an explanatory model, not a claim that every application collects every category of information.",
          },
          {
            type: "table",
            head: ["Interaction stage", "Potential exposure", "Relevant protection area"],
            rows: [
              ["Browse", "Local storage, session history, site permissions", "Session cleanup and browser controls"],
              ["Connect wallet", "Repeated public address, application connection state", "Wallet separation and permissions"],
              ["Use application", "Application data, signatures, authorized actions", "Application isolation and user review"],
              ["Transact", "Public ledger records and observable activity", "Transaction privacy mechanisms with explicit limits"],
            ],
          },
          { type: "p", text: "Figure 2.1 Information exposure across a typical Web3 interaction" },
          { type: "h2", text: "2.8 Zephyr's Design Response" },
          {
            type: "p",
            text: "Zephyr does not assume that one tool can eliminate every privacy risk. Its architecture separates the problem into distinct layers so that protections can be introduced, evaluated, and explained independently.",
          },
          { type: "h3", text: "Session control" },
          {
            type: "p",
            text: "Auto Burn Sessions are designed to clear supported temporary browsing data when a session ends.",
          },
          { type: "h3", text: "Identity separation" },
          {
            type: "p",
            text: "The available Ephemeral Wallet provides a separate Solana wallet context and reduces routine address reuse across supported interactions.",
          },
          { type: "h3", text: "Application boundaries" },
          {
            type: "p",
            text: "Planned isolation capabilities are intended to establish clearer boundaries between decentralized application contexts.",
          },
          { type: "h3", text: "Transaction privacy" },
          {
            type: "p",
            text: "Planned privacy mechanisms are intended to address selected forms of onchain linkage, subject to technical and network limitations.",
          },
          { type: "h3", text: "User authorization" },
          {
            type: "p",
            text: "Sensitive actions should remain subject to explicit user permissions and the controls supported by each feature.",
          },
          {
            type: "note",
            text: "These layers are complementary rather than equivalent. Their effectiveness depends on implementation, user behavior, application design, and the visibility of information outside Zephyr's control.",
          },
        ],
      },
    ],
  },
  {
    group: "Core Infrastructure",
    pages: [
      {
        slug: "architecture",
        chapter: "03",
        title: "Zephyr Architecture and Core Infrastructure",
        blocks: [
          {
            type: "p",
            text: "Zephyr is designed as a privacy oriented browser environment for interacting with websites, decentralized applications, wallets, and supported Solana services. Its architecture distinguishes ordinary browsing activity from sensitive Web3 operations and provides a foundation for additional privacy capabilities as they become available.",
          },
          {
            type: "p",
            text: "This chapter describes the intended logical architecture rather than claiming a finalized implementation. Auto Burn Sessions and the Ephemeral Wallet are available. Transaction privacy, application isolation, bridge, and AI systems described here remain planned components and are marked Coming Soon.",
          },
          { type: "h2", text: "3.1 Architectural Design Principles" },
          {
            type: "p",
            text: "The architecture follows four principles: minimize unnecessary local data retention, establish clear boundaries between application contexts, require explicit user authorization for sensitive operations, and distinguish private browser state from information published to a blockchain.",
          },
          {
            type: "p",
            text: "These principles guide how components should interact. A browsing session can manage its own temporary state, but it cannot independently revoke a completed blockchain transaction or remove information retained by external applications.",
          },
          { type: "h2", text: "3.2 Logical System Layers" },
          {
            type: "p",
            text: "Zephyr can be understood through four logical layers. The diagram illustrates their relationship, not a claim that each planned module is currently deployed.",
          },
          {
            type: "flow",
            vertical: true,
            caption: "Figure 3.1 Conceptual architecture of the Zephyr environment",
            steps: [
              { title: "User Interaction Layer", text: "Browser interface, session controls, permission decisions" },
              { title: "Browser Session Layer", text: "Navigation, temporary state, Auto Burn Sessions" },
              { title: "Web3 Service Layer | Coming Soon", text: "Wallet contexts, dApp isolation, transaction privacy, bridge, AI Agent" },
              { title: "Network and Application Layer", text: "Websites, external services, Solana and supported networks" },
            ],
          },
          { type: "h2", text: "3.3 Browser Session Layer" },
          {
            type: "p",
            text: "The browser session layer manages the user's immediate browsing context. Auto Burn Sessions are intended to clear supported temporary session information when a session ends. The precise categories of information cleared depend on the browser implementation and the relevant storage mechanisms.",
          },
          {
            type: "p",
            text: "Data held by a website, an external service, a connected wallet provider, or a public blockchain is outside the direct scope of local session cleanup. The session layer therefore supports data minimization, not universal erasure.",
          },
          { type: "h2", text: "3.4 Web3 Interaction and Authorization" },
          {
            type: "p",
            text: "Web3 interactions involve distinct operations: viewing application content, connecting a wallet, reviewing transaction instructions, and authorizing signatures. These operations should remain distinguishable so that a browsing action does not silently become permission to transfer assets or deploy a contract.",
          },
          {
            type: "p",
            text: "The intended architecture keeps authorization with the user for sensitive actions. A future AI Agent may prepare or coordinate tasks, but wallet signatures, spending, token deployment, and public posting require permissions appropriate to the action and the connected service.",
          },
          { type: "h2", text: "3.5 Planned Privacy Modules" },
          {
            type: "p",
            text: "The available Ephemeral Wallet supports separate wallet contexts and reduces routine address reuse. Application isolation is intended to reduce unintended sharing of browser context between decentralized applications. These are distinct mechanisms with different technical boundaries.",
          },
          {
            type: "p",
            text: "Each module addresses a different exposure surface. None should be interpreted as a guarantee of anonymity, and their effectiveness will depend on final implementation, network behavior, and user activity.",
          },
          { type: "h2", text: "3.6 Interaction Workflow" },
          {
            type: "p",
            text: "The following workflow shows how a future sensitive Web3 action could be handled conceptually. It is a design model, not a statement of current production behavior.",
          },
          {
            type: "flow",
            vertical: true,
            caption: "Figure 3.2 Conceptual browsing and authorization workflow",
            steps: [
              { title: "1 User Request", text: "Open a supported decentralized application or request an onchain action" },
              { title: "2 Session Context", text: "Establish the active browsing context and applicable session controls" },
              { title: "3 Permission Review", text: "Present the required wallet connection or transaction authorization" },
              { title: "4 Approved Execution", text: "Send the authorized request to the relevant external service or network" },
              { title: "5 Session Closure", text: "Apply supported local cleanup through Auto Burn Sessions" },
            ],
          },
          { type: "h2", text: "3.7 Solana Connectivity" },
          {
            type: "p",
            text: "Solana is Zephyr's primary blockchain environment. Wallet interactions and supported decentralized application requests ultimately depend on Solana network rules and external infrastructure. Public transaction records, account activity, and program interactions may remain observable to third parties even when browser session data is removed.",
          },
          {
            type: "p",
            text: "Any future routing, masking, or mixing capability must be evaluated against these public visibility limits. A change to how a transaction is prepared or routed does not automatically make its onchain effects private.",
          },
          { type: "h2", text: "3.8 Security and Trust Boundaries" },
          {
            type: "p",
            text: "The principal trust boundaries exist between the user and the browser, the browser and external websites, the browser and wallet signing interfaces, and authorized requests and blockchain networks. Third party applications may retain information independently of Zephyr. Users should be able to distinguish actions controlled by the browser from actions performed by external services.",
          },
          {
            type: "p",
            text: "A future implementation should document permission scope, storage handling, transaction review, error behavior, and recovery assumptions for each module. These details are not specified here as completed security guarantees.",
          },
          { type: "h2", text: "3.9 Component Availability" },
          {
            type: "table",
            head: ["Component", "Status"],
            rows: [
              ["Ad free browser experience", "Available"],
              ["Auto Burn Sessions", "Available"],
              ["Ephemeral wallets", "Available"],
              ["Mixer and transaction privacy", "Coming Soon"],
              ["Encrypted dApp isolation", "Coming Soon"],
              ["Metadata protection and MSG Masking", "Coming Soon"],
              ["Cross chain bridge", "Coming Soon"],
              ["Zephyr AI Agent", "Coming Soon"],
            ],
          },
          { type: "h2", text: "3.10 Architecture Summary" },
          {
            type: "p",
            text: "Zephyr's architecture is organized around browser privacy, deliberate authorization, and modular expansion into Web3 services. The immediate foundation includes the browser, Auto Burn Sessions and the available Ephemeral Wallet. Planned modules extend that foundation into application isolation, transaction privacy, cross chain connectivity and intelligent task coordination.",
          },
        ],
      },
      {
        slug: "auto-burn-sessions",
        chapter: "04",
        title: "Auto Burn Sessions and Session Lifecycle",
        blocks: [
          {
            type: "p",
            text: "Auto Burn Sessions are an available privacy capability of the Zephyr browser. Cleanup runs when Zephyr closes and again when Zephyr next starts, allowing residual data from an interrupted or crashed session to be cleared. This reduces retained browser state but does not erase public blockchain activity.",
          },
          { type: "h2", text: "4.1 Purpose and Operating Principle" },
          {
            type: "p",
            text: "A conventional browser may retain information long after a user closes a page. Cookies, cached resources, temporary storage, session identifiers and permission state can contribute to continuity between visits. Some of this persistence is useful for convenience, but it can also extend the lifespan of information that users expected to be temporary.",
          },
          {
            type: "p",
            text: "Zephyr approaches this issue through a defined session lifecycle. A session is treated as a bounded period of activity with a beginning, an active state and a termination event. Auto Burn is intended to initiate cleanup of the temporary data categories governed by that session when the session closes.",
          },
          {
            type: "p",
            text: "The exact scope of deletion depends on the browser implementation and the storage mechanisms involved. A cleanup action should not be interpreted as proof that information was never transmitted to a website, a network provider or a blockchain.",
          },
          { type: "h2", text: "4.2 Session Lifecycle" },
          {
            type: "p",
            text: "The lifecycle below illustrates how Auto Burn fits into a browsing session. Cleanup runs at normal closure and again at the next start to clear residual data, including after a crash.",
          },
          {
            type: "flow",
            caption: "Figure 4.1 Conceptual Auto Burn session lifecycle",
            steps: [
              { title: "01 Session starts", text: "A new browsing context is established." },
              { title: "02 Active browsing", text: "Pages and supported applications operate within the session." },
              { title: "03 Session ends", text: "The browser receives the applicable session termination event." },
              { title: "04 Auto Burn cleanup", text: "Designated temporary session data is cleared." },
              { title: "05 Fresh session", text: "A subsequent session begins without the cleared local state." },
            ],
          },
          { type: "h2", text: "4.3 Data Categories and Boundaries" },
          {
            type: "p",
            text: "A reliable privacy design distinguishes data controlled by the local browser from data maintained by external services. The categories below identify the intended cleanup area and the boundaries that remain outside the feature.",
          },
          {
            type: "table",
            head: ["Information category", "Auto Burn relationship"],
            rows: [
              ["Temporary session storage", "Intended cleanup target where controlled by the session."],
              ["Session cookies and related identifiers", "Potential cleanup target within the browser session boundary."],
              ["Cached session resources", "Potential cleanup target subject to browser storage controls."],
              ["User selected persistent data", "Requires explicit product rules and user controls; not assumed to be deleted."],
              ["Website server logs", "Outside direct browser deletion control."],
              ["Public Solana transactions", "Remain recorded on the blockchain."],
              ["External wallet records", "Governed by the wallet and network, not by local browser cleanup."],
            ],
          },
          { type: "h2", text: "4.4 Session Termination and Cleanup Reliability" },
          {
            type: "p",
            text: "Auto Burn cleanup runs when Zephyr closes and again when Zephyr next starts. This second cleanup pass removes residual session data after a crash or unexpected interruption. The cleanup covers cookies, local storage, browsing history, cache, saved logins, service workers, the DNS cache and open tabs.",
          },
          {
            type: "p",
            text: "The startup cleanup pass addresses residual local data from interrupted sessions. It does not delete copies of information already transmitted to external services or transactions recorded on Solana.",
          },
          { type: "h2", text: "4.5 Relationship to Wallet and dApp Activity" },
          {
            type: "p",
            text: "A browser session can contain interactions with decentralized applications and connected wallets. Clearing browser state may remove some locally retained connection context, but it does not revoke a blockchain transaction, undo an approval already executed onchain or delete records held by a wallet provider.",
          },
          {
            type: "p",
            text: "Where wallet permissions or external sessions persist independently, users may need separate controls to disconnect, revoke or manage them. Zephyr treats these as distinct security concerns rather than presenting local data cleanup as a substitute for authorization management.",
          },
          { type: "h2", text: "4.6 Privacy Scope and User Expectations" },
          {
            type: "p",
            text: "Auto Burn is a data minimization mechanism. Its intended value is to reduce the amount of temporary browser information carried forward between sessions. It is not a complete anonymity system and cannot prevent all forms of tracking while a session is active.",
          },
          {
            type: "p",
            text: "Network addresses, account sign ins, information voluntarily submitted to applications, remote analytics and public transaction history can still create identifiable records. Other Zephyr privacy modules are intended to address different exposure surfaces, and their protections will depend on their eventual design and deployment.",
          },
          { type: "h2", text: "4.7 Operational Summary" },
          {
            type: "table",
            head: ["Principle", "Meaning"],
            rows: [
              ["Bounded sessions", "Browsing activity is organized around a defined session lifecycle."],
              ["Temporary data cleanup", "Designated local session information is cleared at termination."],
              ["Explicit limits", "Remote records and public blockchain history are not erased."],
              ["Separate authorization controls", "Wallet permissions and executed transactions require their own safeguards."],
              ["Implementation transparency", "Cleanup scope and interruption behavior must be documented and tested."],
            ],
          },
          {
            type: "p",
            text: "Auto Burn Sessions establish a practical privacy layer through local session cleanup. The available Ephemeral Wallet provides a separate Solana wallet context. The chapters that follow describe additional planned mechanisms for protecting transactions and isolating decentralized application interactions.",
          },
        ],
      },
    ],
  },
  {
    group: "Privacy Infrastructure",
    pages: [
      {
        slug: "ephemeral-wallets",
        chapter: "05",
        title: "Ephemeral Wallet Infrastructure",
        blocks: [
          { type: "h2", text: "5.1 Overview" },
          {
            type: "p",
            text: "The available Ephemeral Wallet provides a separate Solana wallet context within Zephyr. It supports SOL and token balances, sending SOL and tokens, adding custom tokens, switching between mainnet and devnet, private key import and export, and connecting to supported Solana dApps through approval prompts. Separate wallet contexts can reduce repeated address reuse, but cannot guarantee anonymity.",
          },
          {
            type: "note",
            text: "The Ephemeral Wallet is Available. Session wallet credentials are held only in memory and are destroyed when Zephyr closes unless the private key has been exported. Users must understand the permanent loss risk before transferring assets into a session wallet.",
          },
          { type: "h2", text: "5.2 The Address Reuse Problem" },
          {
            type: "p",
            text: "On Solana, account addresses and transaction records are publicly observable. When the same address interacts with multiple applications, tokens and counterparties, outside observers may correlate those activities. Even when a browser deletes local cookies, the public transaction history associated with a reused address remains available.",
          },
          {
            type: "p",
            text: "Address separation can make direct correlation less straightforward, but it is not equivalent to anonymity. Transfers between addresses, shared funding sources, timing, transaction amounts and application activity may still reveal relationships.",
          },
          { type: "h2", text: "5.3 Wallet Lifecycle and Current Functionality" },
          {
            type: "p",
            text: "A user can use the Ephemeral Wallet to inspect SOL and token balances, send SOL or supported tokens, add custom tokens, switch between Solana mainnet and devnet, import or export a private key, and connect to Solana dApps through approval prompts. The session wallet exists in memory during use. Users must export its private key before closure if they need continued access to the wallet.",
          },
          {
            type: "table",
            head: ["Stage", "Intended function", "User control"],
            rows: [
              ["1. Create", "Establish a distinct wallet identity for a selected activity.", "User initiates or approves creation."],
              ["2. Use", "Connect the selected identity to a supported decentralized application.", "User reviews requested access and signatures."],
              ["3. Manage", "Display balances, transaction status and any remaining permissions.", "User decides whether to continue, transfer or disconnect."],
              ["4. Retire", "End use of the identity and handle local credentials according to the implemented policy.", "User receives clear information about remaining funds and recoverability."],
            ],
          },
          {
            type: "p",
            text: "Figure 5.1. Ephemeral Wallet lifecycle and session key handling.",
          },
          { type: "h2", text: "5.4 Identity Separation and Session Boundaries" },
          {
            type: "p",
            text: "The Ephemeral Wallet separates a browser session wallet from the persistent public blockchain. Closing Zephyr destroys the session wallet private key, but does not remove the address, account balances or historical transactions from Solana.",
          },
          {
            type: "note",
            text: "The session wallet exists only in memory. When Zephyr closes, the private key is destroyed. Any funds left in the wallet become permanently inaccessible unless the key was exported first. Never store funds in a session wallet.",
          },
          { type: "h2", text: "5.5 Key Management and Recovery Considerations" },
          {
            type: "p",
            text: "The Ephemeral Wallet supports private key import and export. A session wallet private key exists only in memory during the session and is destroyed when Zephyr closes. Exporting the key before closure is necessary if the user needs to retain access to that wallet. Imported or exported keys must be handled securely.",
          },
          {
            type: "p",
            text: "Never store funds in a session wallet. Any funds left in it become permanently inaccessible when Zephyr closes unless its private key was exported first. Public account records and balances remain on Solana even when the signing key is destroyed.",
          },
          {
            type: "table",
            head: ["Design consideration", "Required distinction"],
            rows: [
              ["Credential protection", "Local signing secrets require protection independently of browser history cleanup."],
              ["Recovery", "Any backup or export behavior must be clearly disclosed before retirement."],
              ["Asset handling", "Balances and token accounts can persist after a browsing session ends."],
              ["Permissions", "Application connections and delegated authorities may require separate revocation."],
              ["Transparency", "The interface should distinguish address separation from transaction confidentiality."],
            ],
          },
          { type: "h2", text: "5.6 Interaction with Other Zephyr Components" },
          {
            type: "p",
            text: "The available Ephemeral Wallet complements Zephyr's other privacy modules. Auto Burn Sessions address supported temporary browser data. Planned application isolation would address boundaries between decentralized applications, and the proposed Mixer would address selected transaction linkage patterns.",
          },
          {
            type: "p",
            text: "The AI Agent may eventually help users coordinate wallet related tasks through natural language. Any signing, spending, asset transfer or permission change must remain governed by clear authorization boundaries. The complete AI Agent concept is covered in Chapter 10.",
          },
          { type: "h2", text: "5.7 Privacy Limitations and Threat Considerations" },
          {
            type: "p",
            text: "Wallet separation can reduce habitual address reuse, but it cannot erase the public ledger or prevent all forms of correlation. Common funding transactions, network identifiers, application logs, transaction patterns and information voluntarily shared with services may still connect activities. External applications may retain information regardless of Zephyr's local cleanup behavior.",
          },
          {
            type: "p",
            text: "The privacy value of the Ephemeral Wallet depends on how it is funded, used and connected. Zephyr does not treat the creation of a new address as proof of untraceability. Session wallet keys are destroyed when Zephyr closes unless exported first.",
          },
          { type: "h2", text: "5.8 Component Status and Technical Scope" },
          {
            type: "p",
            text: "Ephemeral Wallet Infrastructure is Available. Current functionality includes SOL and token balance display, sending SOL and tokens, adding custom tokens, mainnet and devnet switching, private key import and export, and Solana dApp connections with approval prompts. Session keys are destroyed on browser closure unless exported first.",
          },
          {
            type: "p",
            text: "The goal is to give users more deliberate control over which wallet identities they expose during Web3 interactions, while preserving clear expectations about public blockchain records and the limits of address separation.",
          },
        ],
      },
      {
        slug: "mixer",
        chapter: "06",
        title: "Mixer and Transaction Privacy",
        blocks: [
          { type: "h2", text: "6.1 Purpose and Scope" },
          {
            type: "p",
            text: "The Zephyr Mixer is a proposed privacy component intended to make straightforward association between supported asset movements more difficult. It is marked Coming Soon. This chapter describes the intended privacy objectives, conceptual interaction boundaries, and risks that must be considered before implementation. It does not describe an operational mixing protocol or claim that transactions can become invisible on Solana.",
          },
          {
            type: "p",
            text: "Public blockchain networks preserve transaction records. A mixer cannot erase those records. Its potential value depends on its actual transaction design, the number and behavior of participants, timing patterns, available liquidity, and the information disclosed to external services.",
          },
          { type: "h2", text: "6.2 The Transaction Linkage Problem" },
          {
            type: "p",
            text: "Wallet addresses are pseudonymous identifiers, but repeated use can allow observers to build relationships between addresses, assets, transactions, and decentralized applications. Transfers between wallets may expose direct connections, while transaction timing, amounts, token types, and application interactions can create additional clues.",
          },
          {
            type: "p",
            text: "A new wallet address alone does not guarantee a new private identity. If funds move directly from a known address into a new address, the public transfer may provide a clear association. Zephyr therefore treats address separation and transaction privacy as distinct concerns that require complementary mechanisms.",
          },
          { type: "h2", text: "6.3 Proposed Mixer Function" },
          {
            type: "p",
            text: "The planned mixer is intended to provide a privacy oriented transfer path for supported assets and networks. In a possible user journey, a user would select an eligible asset, review the transaction requirements, authorize a transfer, and receive a clear indication of completion or failure. The specific routing model, custody assumptions, contract design, supported assets, and withdrawal process remain subject to technical specification.",
          },
          {
            type: "p",
            text: "Any deployed mechanism would need to define how assets are held or routed, which parties can access sensitive transaction information, and what safeguards exist against loss, replay, unauthorized withdrawals, or incomplete execution. These decisions cannot be inferred from the term mixer alone.",
          },
          { type: "h2", text: "6.4 Conceptual Transaction Workflow" },
          {
            type: "p",
            text: "Figure 6.1 illustrates the intended user experience at a conceptual level. It is not a depiction of finalized smart contracts, transaction sequencing, or anonymity guarantees.",
          },
          {
            type: "table",
            head: ["Stage", "User or system action", "Privacy and control consideration"],
            rows: [
              ["1. Request", "User selects a supported asset and privacy operation", "Display network, fees, limitations, and risks"],
              ["2. Review", "Zephyr presents the proposed transaction details", "Require explicit user authorization"],
              ["3. Execution", "An approved transaction is submitted to the relevant network", "Public chain records remain observable"],
              ["4. Confirmation", "The system reports the verified outcome", "Avoid claiming privacy that has not been measured"],
              ["5. Session end", "Browser session cleanup runs where applicable", "Cleanup does not remove blockchain history"],
            ],
          },
          { type: "p", text: "Figure 6.1. Conceptual mixer interaction and authorization workflow." },
          { type: "h2", text: "6.5 Relationship to Ephemeral Wallets" },
          {
            type: "p",
            text: "The Ephemeral Wallet is available, while the Mixer remains Coming Soon. The Ephemeral Wallet helps reduce routine address reuse, while the proposed Mixer would address selected transaction linkage patterns. Neither capability erases the public blockchain or guarantees anonymity.",
          },
          {
            type: "p",
            text: "Zephyr should communicate these boundaries before any privacy operation. Users need to understand whether a transfer is supported, whether the operation is reversible, which fees may apply, and what information may remain public or visible to service providers.",
          },
          { type: "h2", text: "6.6 Security and Operational Requirements" },
          {
            type: "p",
            text: "A privacy mechanism that handles assets creates security obligations beyond ordinary browser data cleanup. A production implementation would require documented authorization rules, transaction state handling, failure recovery, asset accounting, and review of any smart contracts or external dependencies. If third party infrastructure is involved, its role and trust assumptions must be disclosed.",
          },
          {
            type: "table",
            head: ["Design area", "Requirement to be specified"],
            rows: [
              ["Authorization", "User approval for spending and transaction submission"],
              ["Asset safety", "Custody model, recovery paths, and contract security"],
              ["Transparency", "Supported assets, network fees, and execution status"],
              ["Privacy analysis", "Known correlation risks and realistic protection boundaries"],
              ["Compliance", "Applicable restrictions and controls for supported jurisdictions"],
              ["Failure handling", "Behavior for failed, delayed, or partially completed operations"],
            ],
          },
          { type: "h2", text: "6.7 Privacy Limitations and Threat Model" },
          {
            type: "p",
            text: "No mixer should be presented as a guarantee of anonymity. Chain analytics may use transaction graphs, distinctive amounts, timing, liquidity flows, and behavior after withdrawal to infer possible relationships. External services may also retain logs, wallet providers may expose connection data, and users may reveal identity through application accounts or repeated habits.",
          },
          {
            type: "p",
            text: "Depending on the eventual design, privacy may also depend on participation levels, routing constraints, or cryptographic assumptions. Until the implementation and independent assessments are available, Zephyr makes no quantitative claim about anonymity sets, unlinkability, or resistance to tracing.",
          },
          { type: "h2", text: "6.8 Status and Documentation Boundary" },
          {
            type: "p",
            text: "Mixer and Transaction Privacy: Coming Soon. This chapter describes an intended component of the Zephyr ecosystem, not a currently available privacy service. Protocol specifications, supported assets, deployment architecture, fee disclosures, security assessments, and measurable privacy properties will require separate technical publication when finalized.",
          },
          {
            type: "p",
            text: "The goal is to give users more control over how they interact with public networks while preserving an accurate understanding of what a browser can protect and what remains permanently visible onchain.",
          },
        ],
      },
      {
        slug: "dapp-isolation",
        chapter: "07",
        title: "Encrypted dApp Isolation and Sandboxing",
        blocks: [
          { type: "h2", text: "7.1 Purpose and Scope" },
          {
            type: "p",
            text: "Encrypted dApp Isolation and Sandboxing is a planned Zephyr privacy capability intended to establish clearer boundaries between decentralized applications, browser session data, and wallet interactions. Its purpose is to reduce unnecessary access to information and limit the extent to which one application can affect another application environment.",
          },
          {
            type: "note",
            text: "This component is Coming Soon. The architecture described here represents intended behavior and design requirements, not a claim that a particular sandbox engine, encryption protocol, or enforcement mechanism has already been implemented.",
          },
          { type: "h2", text: "7.2 Why Application Isolation Matters" },
          {
            type: "p",
            text: "A decentralized application typically operates through a web interface while requesting access to wallet connection information and, in some cases, transaction signing. Websites can also use cookies, local storage, embedded resources, and other browser mechanisms. These data channels can create relationships between visits even when blockchain addresses are changed.",
          },
          {
            type: "p",
            text: "Conventional browser protections already restrict many forms of access between different origins. Zephyr aims to complement those protections with explicit session and application boundaries. Isolation must not be presented as a substitute for standard browser security controls or wallet signature verification.",
          },
          { type: "h2", text: "7.3 Proposed Isolation Model" },
          {
            type: "p",
            text: "The planned model separates three concerns: the application context, the session context, and the wallet authorization context. Application resources and permissions should remain scoped to the relevant context wherever technically feasible. Sensitive actions should pass through deliberate user approval rather than being inferred from a previously connected website.",
          },
          {
            type: "p",
            text: "Encryption may protect selected information while it is stored or transmitted, but encryption alone does not prevent tracking, malicious scripts, or unauthorized signing. Specific cryptographic methods and key handling rules will be documented when the implementation is finalized.",
          },
          { type: "h2", text: "7.4 Conceptual Application Interaction Workflow" },
          {
            type: "p",
            text: "Figure 7.1 illustrates a proposed interaction sequence. The diagram is a functional model rather than a specification of deployed software components.",
          },
          {
            type: "flow",
            vertical: true,
            caption: "Figure 7.1. Conceptual dApp isolation and user authorization workflow.",
            steps: [
              { title: "01 Open dApp", text: "Zephyr creates or selects a scoped application context." },
              { title: "02 Apply Session Boundaries", text: "Supported storage and application permissions are associated with that context." },
              { title: "03 Request Wallet Access", text: "The dApp asks to connect or perform an authorized operation." },
              { title: "04 Review and Approve", text: "The user evaluates the request and confirms any required wallet signature." },
              { title: "05 End or Change Session", text: "Supported temporary session information is cleared according to session rules." },
            ],
          },
          { type: "h2", text: "7.5 Storage, Permissions, and Session Boundaries" },
          {
            type: "p",
            text: "A privacy oriented browser should identify which information is temporary, which information belongs to a specific application, and which information the user deliberately chooses to retain. Relevant categories may include local website data, cached resources, connection state, and permissions granted during a session.",
          },
          {
            type: "p",
            text: "The planned isolation capability is intended to reduce unintended reuse of such information across separate contexts. Its actual protection depends on browser platform constraints, supported applications, and how wallet providers expose connection state. The browser cannot assume that a remote dApp deletes information it has already received.",
          },
          { type: "h2", text: "7.6 Wallet Interaction and Signing Controls" },
          {
            type: "p",
            text: "An isolated dApp environment must still allow legitimate transactions. Zephyr therefore distinguishes website access from transaction authorization. A dApp may prepare a request, but a wallet signature or asset transfer must remain subject to the applicable user consent process.",
          },
          {
            type: "p",
            text: "Isolation does not establish that a transaction is safe. Users may still encounter malicious contracts, deceptive interfaces, harmful approvals, or misleading transaction descriptions. Clear request presentation, permission visibility, and deliberate signing remain essential.",
          },
          { type: "h2", text: "7.7 Relationship to Auto Burn Sessions" },
          {
            type: "p",
            text: "Auto Burn Sessions and dApp isolation address related but different risks. Auto Burn is intended to clean up supported temporary browsing information at session termination. Application isolation is intended to control boundaries while a session is active. Together, these mechanisms may reduce unnecessary data persistence and cross application exposure.",
          },
          {
            type: "p",
            text: "Neither mechanism can revoke information already transmitted to external servers or remove transactions recorded on Solana. Users may also need to manage permissions or authorizations that persist outside the browser.",
          },
          { type: "h2", text: "7.8 Security Boundaries and Limitations" },
          {
            type: "p",
            text: "The intended design must account for malicious application scripts, embedded third party resources, browser vulnerabilities, wallet provider behavior, and information leaked through network connections. An isolated context cannot by itself hide a public IP address, prevent all browser fingerprinting, or guarantee that a website cannot identify a returning user.",
          },
          {
            type: "p",
            text: "Formal security guarantees require an implemented architecture, defined threat model, and appropriate testing. Zephyr will distinguish confirmed protections from planned mechanisms as those technical details become available.",
          },
          { type: "h2", text: "7.9 Component Status" },
          {
            type: "table",
            head: ["Capability", "Status"],
            rows: [
              ["Auto Burn Sessions", "Available"],
              ["Encrypted dApp Isolation", "Coming Soon"],
              ["Scoped dApp Permissions", "Coming Soon"],
              ["Additional Application Sandboxing", "Coming Soon"],
            ],
          },
          {
            type: "p",
            text: "The exact isolation methods, encryption design, and supported application behaviors remain subject to technical implementation and validation.",
          },
        ],
      },
      {
        slug: "metadata-protection",
        chapter: "08",
        title: "Metadata Protection and MSG Masking",
        blocks: [
          { type: "h2", text: "8.1 Purpose and Scope" },
          {
            type: "p",
            text: "Metadata Protection and MSG Masking are planned privacy capabilities within the Zephyr browser architecture. They are intended to reduce unnecessary disclosure of contextual information during browsing and supported Web3 interactions. Both capabilities are Coming Soon. The descriptions in this chapter establish intended design boundaries, not a claim that a particular implementation has been deployed or independently verified.",
          },
          {
            type: "p",
            text: "Metadata protection addresses information surrounding an interaction, such as timing, application context, connection attributes, and session identifiers. MSG Masking addresses the presentation and handling of selected transaction instruction or execution details within supported workflows. These are distinct concerns and neither capability should be interpreted as a method for removing information from the public Solana ledger.",
          },
          { type: "h2", text: "8.2 Understanding Metadata Exposure" },
          {
            type: "p",
            text: "A decentralized interaction can reveal more than the explicit content submitted by a user. Websites, network intermediaries, wallet providers, and blockchain observers may each encounter different portions of an activity record. The resulting information can sometimes be correlated across sessions or applications, even where individual data elements appear innocuous.",
          },
          {
            type: "p",
            text: "Examples include browser identifiers, connection metadata, referral context, wallet connection requests, transaction timing, destination programs, and publicly visible account activity. Zephyr cannot control every observer or external service. Its design goal is to limit exposure at points where the browser has a meaningful opportunity to intervene.",
          },
          { type: "h2", text: "8.3 Planned Metadata Protection Model" },
          {
            type: "p",
            text: "The proposed metadata protection layer would evaluate what contextual information a browser interaction needs to disclose and whether unnecessary data can be omitted, isolated, or handled within a temporary session context. Controls may differ depending on the website, wallet integration, network connection, and application requirements.",
          },
          {
            type: "table",
            head: ["Exposure surface", "Intended protection objective"],
            rows: [
              ["Browser session context", "Reduce unnecessary persistence and reuse of temporary identifiers."],
              ["Application context", "Limit unintended sharing of contextual information across supported dApp environments."],
              ["Wallet connection context", "Keep authorization decisions explicit and avoid assuming that one connection authorizes unrelated applications."],
              ["Transaction preparation", "Present relevant transaction information to the user without exposing additional local context unnecessarily."],
              ["Network and external services", "Document exposure that remains outside the browser's control."],
            ],
          },
          {
            type: "p",
            text: "Figure 8.1. Conceptual metadata protection surfaces. This table describes design objectives rather than confirmed enforcement mechanisms.",
          },
          { type: "h2", text: "8.4 MSG Masking Concept" },
          {
            type: "p",
            text: "MSG Masking is a proposed Zephyr mechanism concerned with limiting unnecessary exposure of selected transaction message or instruction details during supported preparation and interaction flows. Its precise technical scope, supported transaction types, and enforcement mechanisms have not yet been finalized.",
          },
          {
            type: "p",
            text: "On Solana, transaction messages and executed instructions may be publicly inspectable through the blockchain. A browser feature cannot make public transaction data secret merely by hiding it in the interface or changing how it is displayed before signing. Any future MSG Masking implementation must distinguish local presentation controls from actual cryptographic or protocol level confidentiality.",
          },
          {
            type: "p",
            text: "For this reason, Zephyr treats MSG Masking as a planned privacy capability with clearly defined technical limitations, rather than as a guarantee that transaction instructions or execution details become invisible to validators, explorers, or other blockchain observers.",
          },
          { type: "h2", text: "8.5 Conceptual Interaction Workflow" },
          {
            type: "p",
            text: "Figure 8.2 shows how metadata review and transaction handling could fit within a user controlled browser workflow. Each stage represents a design responsibility, not a finalized internal component or a promise of confidential onchain execution.",
          },
          {
            type: "table",
            head: ["Stage", "Browser responsibility", "User or network boundary"],
            rows: [
              ["1. Interaction request", "Receive the dApp request within the active session.", "The requesting application remains an external trust boundary."],
              ["2. Context review", "Identify contextual information relevant to the request.", "External network metadata may remain observable."],
              ["3. Disclosure controls", "Apply supported metadata restrictions where technically possible.", "Essential protocol fields may still be required."],
              ["4. Transaction review", "Present transaction details and obtain explicit authorization.", "The user retains control of approval and signing."],
              ["5. Network submission", "Submit only an authorized transaction through supported connectivity.", "Public Solana transaction information remains observable."],
              ["6. Session cleanup", "Apply Auto Burn cleanup to supported temporary session data.", "Cleanup does not erase external logs or ledger history."],
            ],
          },
          {
            type: "p",
            text: "Figure 8.2. Proposed interaction workflow for metadata review, authorization, and session cleanup.",
          },
          { type: "h2", text: "8.6 Relationship to Other Zephyr Components" },
          {
            type: "p",
            text: "Metadata Protection is designed to complement Auto Burn Sessions by addressing what may be exposed during an interaction, while Auto Burn addresses supported temporary information retained after the session. Planned application isolation would establish additional boundaries between dApp contexts. Ephemeral Wallets would address repeated address use, while the proposed mixer would target certain forms of transaction linkage. Each component addresses a different privacy problem and cannot substitute for the others.",
          },
          {
            type: "p",
            text: "The AI Agent, when available, would also need to respect these boundaries. Automated browsing or transaction preparation must not silently override privacy controls, wallet authorization, or explicit signing requirements.",
          },
          { type: "h2", text: "8.7 Security, Transparency, and Limitations" },
          {
            type: "p",
            text: "Metadata minimization does not guarantee anonymity. Correlation may remain possible through IP addresses, device characteristics, account identifiers, behavioral patterns, public wallet activity, and third party records. Likewise, the existence of a masking control does not imply that the underlying blockchain data is confidential.",
          },
          {
            type: "p",
            text: "Any eventual implementation should document supported environments, which fields can be controlled, which observers remain able to see activity, and whether a protection operates locally, through an external service, or at the protocol level. Where protection is unavailable, the interface should not suggest otherwise.",
          },
          { type: "h2", text: "8.8 Component Status" },
          {
            type: "table",
            head: ["Component", "Status", "Documentation position"],
            rows: [
              ["Metadata Protection", "Coming Soon", "Design objectives and exposure boundaries only."],
              ["MSG Masking", "Coming Soon", "Conceptual scope; implementation and guarantees not finalized."],
              ["Auto Burn Sessions", "Available", "Cleanup of supported temporary browsing session data."],
            ],
          },
          {
            type: "p",
            text: "The goal of these planned systems is to reduce avoidable information disclosure while preserving clear user consent and accurate expectations about what remains publicly or externally observable.",
          },
        ],
      },
    ],
  },
  {
    group: "Connectivity & AI",
    pages: [
      {
        slug: "solana-and-bridge",
        chapter: "09",
        title: "Solana Connectivity and Cross Chain Bridge",
        blocks: [
          { type: "h2", text: "9.1 Purpose and Scope" },
          {
            type: "p",
            text: "Solana is the primary blockchain environment for Zephyr. The browser is intended to provide a consistent interface for accessing compatible decentralized applications, reviewing wallet connection requests, and initiating authorized blockchain interactions. Cross chain bridging is a planned extension of this connectivity model and is marked Coming Soon.",
          },
          {
            type: "p",
            text: "This chapter distinguishes the connectivity functions of a browser from the actions performed by a wallet, a decentralized application, a bridge service, and the Solana network. A browser interface can present information and coordinate requests, but it does not independently validate the economic safety of every application or reverse a confirmed blockchain transaction.",
          },
          { type: "h2", text: "9.2 Solana Connection Model" },
          {
            type: "p",
            text: "A typical Solana interaction begins when a user opens a decentralized application. The application may request a wallet connection, propose a message signature, or prepare a transaction. Zephyr is intended to make these requests understandable within the browsing experience while keeping final authorization under the user's control.",
          },
          {
            type: "p",
            text: "Transaction processing remains subject to wallet implementation, application logic, network availability, transaction fees, and the consensus rules of Solana. A successful connection does not establish that an application is trustworthy.",
          },
          {
            type: "table",
            head: ["Stage", "Responsible component", "Primary control"],
            rows: [
              ["1 Open application", "Zephyr browser", "Present the requested website and its origin"],
              ["2 Request connection", "Decentralized application", "Declare requested wallet access"],
              ["3 Review request", "User and wallet interface", "Approve or reject the requested action"],
              ["4 Submit transaction", "Wallet and network interface", "Transmit an authorized transaction"],
              ["5 Confirm outcome", "Solana network and application", "Report confirmation or failure"],
            ],
          },
          {
            type: "p",
            text: "Figure 9.1 Conceptual Solana interaction flow. The diagram is conceptual. Actual wallet providers and applications may use different interfaces, and the browser must not be treated as a substitute for independent transaction review.",
          },
          { type: "h2", text: "9.3 Wallet Permissions and Transaction Authorization" },
          {
            type: "p",
            text: "Connection permission, message signing, and transaction signing are separate decisions. Connecting a wallet can reveal its public address to an application. Signing a message may authenticate an account or authorize an application specific action. Signing a transaction can result in an irreversible transfer or a change to onchain state.",
          },
          {
            type: "p",
            text: "Zephyr's available Ephemeral Wallet supports a separate wallet context for supported interactions. Address separation may reduce repeated address reuse, but public funding transfers and other onchain activity can still reveal relationships.",
          },
          { type: "h2", text: "9.4 Cross Chain Bridge Architecture" },
          {
            type: "p",
            text: "The planned bridge is intended to provide a coordinated interface for moving supported assets between Solana and other supported networks. Bridge availability, supported networks, asset coverage, custody assumptions, routing design, and service providers have not been finalized. These details will be documented when the implementation is ready.",
          },
          {
            type: "p",
            text: "A cross chain transfer generally involves a source network action, a verification or messaging mechanism, and a destination network outcome. Different bridge designs may lock, burn, mint, release, or transfer assets through liquidity arrangements. Zephyr does not currently commit to any particular mechanism.",
          },
          {
            type: "table",
            head: ["Step", "User experience", "Technical consideration"],
            rows: [
              ["Select route", "Choose source network, destination network, and asset", "Confirm that the route and asset are supported"],
              ["Review terms", "Inspect estimated costs, destination, and risk disclosures", "Fees, liquidity, and settlement behavior may vary"],
              ["Authorize", "Approve the required wallet actions", "Authorization remains with the user"],
              ["Transfer", "Observe source and destination progress", "Completion depends on the selected bridge mechanism"],
              ["Verify receipt", "Check the destination wallet and network", "Network finality and destination settlement may differ"],
            ],
          },
          { type: "p", text: "Figure 9.2 Proposed bridge workflow" },
          { type: "h2", text: "9.5 Bridge Security and Failure Conditions" },
          {
            type: "p",
            text: "Bridging introduces risks beyond ordinary browsing, including smart contract vulnerabilities, message verification failures, malicious token representations, liquidity shortages, unsupported destinations, and transaction delays. An interface should clearly distinguish a pending transfer from a completed one and should not present an unverified destination balance as settled funds.",
          },
          {
            type: "p",
            text: "Recovery procedures depend on the actual bridge implementation. Until a supported bridge and its operational model are published, Zephyr cannot promise reversibility, guaranteed settlement times, or recovery of assets sent through incorrect routes.",
          },
          { type: "h2", text: "9.6 Relationship to Privacy Infrastructure" },
          {
            type: "p",
            text: "The planned bridge may eventually interact with the available Ephemeral Wallet and other privacy components. These systems address different exposure points. A separate wallet address can reduce direct address reuse, while browser session cleanup removes supported local data without erasing public transactions.",
          },
          {
            type: "p",
            text: "Users should assume that public blockchain records, bridge messages, transaction amounts, and timing information may remain observable. Any stronger privacy properties would require a separately specified and evaluated mechanism.",
          },
          { type: "h2", text: "9.7 Component Availability" },
          {
            type: "table",
            head: ["Component", "Status", "Documentation boundary"],
            rows: [
              ["Solana browser connectivity", "Available", "Solana connectivity and supported dApp wallet approval prompts are available"],
              ["Cross chain bridge", "Coming Soon", "Supported routes and bridge architecture not yet published"],
              ["Ephemeral Wallets", "Available", "Address separation does not guarantee anonymity"],
              ["Transaction privacy modules", "Coming Soon", "No claim of invisible or untraceable transfers"],
            ],
          },
          { type: "h2", text: "9.8 Technical Summary" },
          {
            type: "p",
            text: "Zephyr's Solana connectivity is intended to make decentralized application interaction more coherent within a privacy focused browser. The proposed bridge would extend this experience across supported networks while preserving clear user authorization and transparent transaction status. Its final design, supported networks, security assumptions, and operational requirements remain subject to implementation and will be described when available.",
          },
        ],
      },
      {
        slug: "ai-agent",
        chapter: "10",
        title: "Zephyr AI Agent",
        blocks: [
          { type: "h2", text: "10.1 Purpose and Scope" },
          {
            type: "p",
            text: "The Zephyr AI Agent is a planned browser native intelligence layer intended to help users move from an instruction to a useful result. Its scope extends beyond answering questions. The proposed system brings together contextual browsing, creative production, project development, connected services and authorized blockchain operations within a single user environment.",
          },
          {
            type: "note",
            text: "The AI Agent is Coming Soon. The capabilities described in this chapter are intended product functions rather than a statement that each integration, execution path or creative service is currently available. Actual behavior will depend on supported platforms, technical constraints and user permissions.",
          },
          { type: "h2", text: "10.2 Intent Driven Browser Operations" },
          {
            type: "p",
            text: "Users are intended to express goals in natural language. The Agent would interpret the request, identify relevant pages and services, organize the required actions and present a clear plan when the task involves consequential steps. Supported operations may include navigating websites, summarizing information, preparing content, interacting with decentralized applications and coordinating tasks that span multiple interfaces.",
          },
          {
            type: "p",
            text: "A distinction is maintained between reading information, preparing an action and executing it. A generated plan or transaction proposal is not the same as an authorized transaction. Wallet signatures, asset transfers, token deployment and publishing to external accounts must remain subject to explicit user permission and the applicable platform controls.",
          },
          {
            type: "flow",
            caption: "Figure 10.1. Conceptual AI Agent task workflow. Each stage remains subject to user permissions and implementation details.",
            steps: [
              { title: "User intent" },
              { title: "Context and task planning" },
              { title: "Creative or browser actions" },
              { title: "User review and authorization" },
              { title: "Execution and result" },
            ],
          },
          { type: "h2", text: "10.3 Project Creation from a Single Idea" },
          {
            type: "p",
            text: "The Agent is envisioned as a creation environment for Solana projects. A creator could describe a theme, community concept or character and receive a coherent project proposal. The proposal may contain candidate token names and symbols, a project description, visual direction, mascot concepts, lore, messaging and a suggested online presence.",
          },
          {
            type: "p",
            text: "The creator remains responsible for choosing the final concept and approving public claims. Generated material should be editable and revisable. The goal is to reduce the work required to coordinate different creative tools, not to remove the creator from meaningful decisions.",
          },
          { type: "h2", text: "10.4 Brand Awareness and Intellectual Property Screening" },
          {
            type: "p",
            text: "Before a creator adopts a proposed identity, the Agent is intended to help surface potentially confusingly similar project names, brand references, copyrighted characters and other recognizable visual elements. It may suggest more distinctive alternatives and identify possible impersonation or platform policy concerns.",
          },
          {
            type: "p",
            text: "Such screening is advisory. A shared token symbol does not automatically establish trademark infringement, and a similarity search cannot provide legal clearance. Trademark rights, copyright rights and platform moderation rules are separate considerations. Creators should obtain appropriate professional review when legal certainty is needed.",
          },
          { type: "h2", text: "10.5 Creative Production and Project Presence" },
          {
            type: "p",
            text: "The planned creative system extends across visual assets and public communication. Depending on supported generation services, the Agent may prepare logos, banners, website concepts, memes, videos, social posts and campaign materials. The intended benefit is consistency across formats: a project should feel like one recognizable identity rather than a collection of unrelated outputs.",
          },
          {
            type: "p",
            text: "Generated websites and public content must remain subject to creator review before deployment or publication. Content generation also requires practical safeguards for misleading claims, unauthorized use of third party intellectual property and unsafe external actions.",
          },
          {
            type: "table",
            head: ["Creative output", "Intended function"],
            rows: [
              ["Project identity", "Names, narrative, visual language and character direction"],
              ["Website", "Project page structure, copy and visual assets"],
              ["Images and memes", "Campaign graphics and community content"],
              ["Video", "Short promotional concepts and generated media"],
              ["Social content", "Draft posts, announcements and ongoing campaigns"],
            ],
          },
          { type: "h2", text: "10.6 Persistent Project Identity" },
          {
            type: "p",
            text: "A defining objective is continuity. Each supported project would have a dedicated identity profile containing its character description, tone, visual preferences, lore, approved terminology, creator instructions and relevant past activity. Future creative tasks could refer to this profile so that the same project retains a recognizable voice across websites, media and social channels.",
          },
          {
            type: "p",
            text: "Separate projects should have separate identity contexts. Project memory should not be treated as a substitute for consent or unrestricted access to private information. The exact storage, retention, export and deletion controls for persistent identity data will be specified when the feature is introduced.",
          },
          {
            type: "flow",
            caption: "Figure 10.2. Conceptual project identity and content production cycle. Each stage remains subject to user permissions and implementation details.",
            steps: [
              { title: "Creator brief" },
              { title: "Project identity profile" },
              { title: "Content generation" },
              { title: "Creator review" },
              { title: "Approved publication" },
              { title: "Identity continuity" },
            ],
          },
          { type: "h2", text: "10.7 Connected Social Accounts and Ongoing Management" },
          {
            type: "p",
            text: "With supported account connections, the Agent is intended to assist with drafting, planning, scheduling and publishing project communications. The system may also help organize recurring campaigns and review community interaction opportunities. Supported platforms and the scope of account access will depend on available integrations and their permission models.",
          },
          {
            type: "p",
            text: "Connection to a social account must not imply unrestricted authority to post. Creators should be able to review or revise drafts, approve publication and revoke access. Automated community engagement must follow the rules of the relevant platform and avoid deceptive impersonation or fabricated performance claims.",
          },
          { type: "h2", text: "10.8 Solana Token Deployment and Authorized Execution" },
          {
            type: "p",
            text: "Token creation is one planned use case within the broader Agent experience. For supported launches, Zephyr intends to integrate with StonkFun so that a creator can move from an approved project concept toward token deployment on Solana. The Agent may help assemble required launch details, review creator selections and coordinate the supported launch interface.",
          },
          {
            type: "p",
            text: "The creator must approve material deployment details and any required wallet transaction. Network fees, platform charges, launch conditions and transaction outcomes are determined by the applicable services and blockchain state. The Agent cannot guarantee successful deployment, token liquidity, market demand or financial returns.",
          },
          {
            type: "p",
            text: "The Agent may also coordinate other authorized browser and Solana activities and, where available, interact with Zephyr privacy modules. Those modules are distinct systems with their own availability and limitations. AI assistance does not make public blockchain records private.",
          },
          { type: "h2", text: "10.9 Human Oversight and Operating Boundaries" },
          {
            type: "p",
            text: "The Agent is intended to reduce repetitive work while preserving user control. Information retrieval and drafting can be handled differently from actions that create legal, financial or reputational consequences. The system should clearly distinguish a suggested action from an executed action and expose enough context for users to understand what they are approving.",
          },
          {
            type: "table",
            head: ["Action class", "Intended control"],
            rows: [
              ["Research and summarization", "Display sources and allow user verification"],
              ["Creative drafts", "Allow editing and approval before external use"],
              ["Social publication", "Require appropriate account permissions and publication controls"],
              ["Wallet operations", "Require user authorization for relevant signatures and spending"],
              ["Token deployment", "Require confirmation of launch details and applicable transactions"],
            ],
          },
          { type: "h2", text: "10.10 Agent Architecture and Economic Framework" },
          {
            type: "p",
            text: "Zephyr envisions coordinated capabilities for task planning, creative generation, project identity management, browser interaction and supported onchain execution. These capabilities may be organized into specialized components, but the implementation architecture has not been finalized for public specification.",
          },
          {
            type: "note",
            text: "Coming Soon. Detailed specifications for agent coordination, operational permissions, supported integrations, service fees, revenue distribution and the associated economic framework will be published with the AI Agent introduction. No specific fee schedule, revenue allocation or token economic relationship is established by this chapter.",
          },
          { type: "h2", text: "10.11 Capability Status and Summary" },
          {
            type: "table",
            head: ["Capability", "Status"],
            rows: [
              ["Natural language browser workflows", "Coming Soon"],
              ["Project concept and brand creation", "Coming Soon"],
              ["Intellectual property awareness", "Coming Soon"],
              ["Websites, images, memes and videos", "Coming Soon"],
              ["Persistent project identities", "Coming Soon"],
              ["Connected social management", "Coming Soon"],
              ["StonkFun token launch coordination", "Coming Soon"],
              ["Agent architecture and economic details", "Coming Soon"],
            ],
          },
          {
            type: "p",
            text: "The Zephyr AI Agent is designed to connect creation, execution and ongoing management within the browser. Its purpose is not limited to launching tokens. It is intended to help users create an identity, build a digital presence, coordinate authorized actions and sustain a project over time while keeping consequential decisions under user control.",
          },
        ],
      },
    ],
  },
  {
    group: "Security & Control",
    pages: [
      {
        slug: "security",
        chapter: "11",
        title: "Security Architecture and Threat Model",
        blocks: [
          { type: "h2", text: "11.1 Security Objectives and Scope" },
          {
            type: "p",
            text: "Zephyr treats browser security as a combination of data handling, permission control, application boundaries and informed user decisions. The objective is to reduce avoidable exposure during Web3 activity while making sensitive actions understandable and deliberate. Security measures described as planned in this chapter are architectural intentions, not claims of completed implementation or independent audit.",
          },
          {
            type: "p",
            text: "The current browser foundation includes Auto Burn Sessions, an ad free experience and the available Ephemeral Wallet. Additional application isolation, transaction privacy and intelligent execution capabilities remain Coming Soon. The security posture of each component must be evaluated independently.",
          },
          { type: "h2", text: "11.2 Assets and Trust Boundaries" },
          {
            type: "p",
            text: "The assets requiring protection include temporary browsing information, session identifiers, connected wallet permissions, transaction instructions, account credentials, generated project materials and any information provided to the AI Agent. Public blockchain information is not private browser data and cannot be removed by a browser cleanup operation.",
          },
          {
            type: "table",
            head: ["Boundary", "Protected interest", "Primary consideration"],
            rows: [
              ["Browser session", "Temporary local state", "Data lifetime and cleanup coverage"],
              ["Website and dApp", "Site access and script behavior", "Origin separation and permission scope"],
              ["Wallet connection", "Signing authority and account access", "Explicit consent and transaction review"],
              ["Solana network", "Transaction submission and confirmation", "Public visibility and irreversible settlement"],
              ["Connected services", "Credentials and shared content", "Access scope and revocation"],
              ["AI Agent", "Intent interpretation and tool use", "Approval gates and action logging"],
            ],
          },
          { type: "p", text: "Table 11.1 Conceptual trust boundaries and the security questions associated with each." },
          { type: "h2", text: "11.3 Threat Model" },
          {
            type: "p",
            text: "The threat model considers both malicious activity and ordinary user mistakes. Relevant actors may include deceptive websites, compromised extensions or devices, malicious dApps, fraudulent token issuers, untrusted external services and attackers who correlate public blockchain activity with offchain information.",
          },
          {
            type: "table",
            head: ["Threat scenario", "Potential consequence", "Intended defense or user control"],
            rows: [
              ["Persistent browser traces", "Exposure of previous activity on a shared device", "Auto Burn cleanup of supported temporary session data"],
              ["Malicious dApp request", "Unexpected account access or signature", "Scoped permissions and clear approval prompts"],
              ["Cross application data leakage", "Correlation between otherwise separate activities", "Available Ephemeral Wallet with explicit privacy limits"],
              ["Address reuse and correlation", "Linkage of public onchain actions", "Planned Ephemeral Wallet options with explicit privacy limits"],
              ["Misleading transaction instructions", "Unauthorized transfer or contract interaction", "Readable transaction review and user confirmation"],
              ["Agent acting beyond user intent", "Unwanted posting, deployment or spending", "Planned tool permissions and approval gates"],
              ["Compromised device or recovery secret", "Loss of control over assets", "Device hygiene and secure key handling remain essential"],
            ],
          },
          {
            type: "p",
            text: "Table 11.2 Representative threats and proposed mitigations. These are design objectives, not verified controls.",
          },
          { type: "h2", text: "11.4 Security Control Flow" },
          {
            type: "p",
            text: "The following conceptual flow shows where the browser should separate observation, preparation and authorization. The user remains the final authority for sensitive external actions.",
          },
          {
            type: "table",
            head: ["Stage", "Security decision"],
            rows: [
              ["1. User intent", "Identify the requested outcome and affected accounts or services"],
              ["2. Context inspection", "Determine the requesting website, tool or connected application"],
              ["3. Action preparation", "Construct a preview of permissions, destination and expected effects"],
              ["4. Risk review", "Surface uncertain details, unusual permissions and irreversible actions"],
              ["5. User approval", "Require explicit authorization for signing, spending, deployment or publishing"],
              ["6. Execution and result", "Submit only the approved action and report the observed result"],
            ],
          },
          {
            type: "p",
            text: "Figure 11.1. Conceptual sensitive action authorization workflow. Exact enforcement mechanisms depend on the relevant implementation.",
          },
          { type: "h2", text: "11.5 Session Cleanup and Residual Risk" },
          {
            type: "p",
            text: "Auto Burn cleanup runs when Zephyr closes and again when it next starts, including after an application crash. It clears cookies, local storage, browsing history, cache, saved logins, service workers, the DNS cache and open tabs. This does not erase server logs or public blockchain records.",
          },
          {
            type: "p",
            text: "Auto Burn does not revoke permissions already granted on a blockchain, erase server logs maintained by a website, remove public transaction records or secure a device that is already compromised. Users may still need to disconnect wallets, revoke approvals through appropriate tools and protect recovery information.",
          },
          { type: "h2", text: "11.6 Wallet and Transaction Safeguards" },
          {
            type: "p",
            text: "Wallet interactions should distinguish connecting an account from authorizing a message and signing a transaction. Each action has different implications. A connected dApp may learn an account address, while a signed transaction may create permanent public effects. Transaction previews should make the requested operation and its consequences as clear as practical.",
          },
          {
            type: "p",
            text: "The available Ephemeral Wallet provides a separate Solana wallet context, but cannot guarantee anonymity or prevent all chain analysis. Its session private key is destroyed on browser closure. Funds remaining in a session wallet become inaccessible unless the key was exported beforehand.",
          },
          { type: "h2", text: "11.7 Application Isolation and External Dependencies" },
          {
            type: "p",
            text: "Encrypted dApp Isolation and Sandboxing are planned to limit unnecessary sharing of application state and privileges. The intended model separates website context, sensitive browser operations and wallet authorization rather than treating all connected applications as equally trusted.",
          },
          {
            type: "p",
            text: "Actual isolation strength will depend on browser engine behavior, process boundaries, storage partitioning, extension policies and the final implementation. Network providers, remote procedure call endpoints, bridge services and connected social platforms remain external trust dependencies.",
          },
          { type: "h2", text: "11.8 AI Agent Security and Permission Model" },
          {
            type: "p",
            text: "The planned Zephyr AI Agent may interpret instructions, prepare content, interact with websites and initiate authorized Solana operations. Its security model must distinguish low risk assistance, such as summarizing a page, from high impact actions, such as publishing to a connected account, deploying a token or transferring assets.",
          },
          {
            type: "p",
            text: "The intended safeguards include scoped tool access, visible action previews, approval for sensitive operations and controls to stop or revoke ongoing access. Generated content and proposed actions should be treated as suggestions until reviewed. Detailed enforcement mechanisms, agent architecture and operational permissions will be documented when the Agent is introduced.",
          },
          { type: "h2", text: "11.9 Security Validation and Disclosure" },
          {
            type: "p",
            text: "A credible security program requires implementation review, testing of permission boundaries, evaluation of session cleanup behavior and assessment of external integrations. Where appropriate, independent audits and clear vulnerability reporting processes should be considered. This documentation does not imply that an audit, certification or penetration test has already occurred.",
          },
          {
            type: "p",
            text: "Security is an ongoing property of software and operations rather than a permanent guarantee. Users should be able to understand which protections are available, which are planned and where risks remain.",
          },
          { type: "h2", text: "11.10 Component Security Status" },
          {
            type: "table",
            head: ["Component", "Status", "Security scope"],
            rows: [
              ["Auto Burn Sessions", "Available", "Supported temporary session cleanup"],
              ["Ad free browser", "Available", "Browsing without integrated advertising"],
              ["Ephemeral Wallets", "Available", "Optional wallet identity separation"],
              ["Mixer and transaction privacy", "Coming Soon", "Proposed transaction linkage reduction"],
              ["Encrypted dApp Isolation", "Coming Soon", "Application and permission boundaries"],
              ["Metadata Protection and MSG Masking", "Coming Soon", "Proposed reduction of selected exposure surfaces"],
              ["Cross Chain Bridge", "Coming Soon", "Asset transfer across supported networks"],
              ["Zephyr AI Agent", "Coming Soon", "Authorized browser and onchain task execution"],
            ],
          },
          { type: "p", text: "Table 11.3 Availability statements do not represent security certifications." },
        ],
      },
      {
        slug: "privacy-boundaries",
        chapter: "12",
        title: "Privacy Boundaries and User Control",
        blocks: [
          { type: "h2", text: "12.1 Purpose and Scope" },
          {
            type: "p",
            text: "Privacy in Zephyr is defined by the information a particular component can limit, isolate, or remove. No single feature can eliminate every source of exposure. Browser data, wallet identifiers, application permissions, network traffic, and public blockchain transactions exist in different environments and require different controls.",
          },
          {
            type: "p",
            text: "This chapter explains the intended boundaries of Zephyr privacy features and the decisions that remain with the user. It distinguishes capabilities available in the initial browser from proposed components marked Coming Soon. The descriptions of planned components represent design objectives rather than verified implementation guarantees.",
          },
          { type: "h2", text: "12.2 The Layers of Privacy" },
          {
            type: "p",
            text: "Browser session privacy concerns local information such as temporary storage, browsing state, and other supported session artifacts. Auto Burn Sessions are designed to remove supported temporary data when a session ends. The exact cleanup coverage depends on the browser implementation, platform restrictions, and the type of information involved.",
          },
          {
            type: "p",
            text: "Application privacy concerns what decentralized applications can access during a session, including wallet addresses, connection permissions, and information users voluntarily provide. Planned application isolation is intended to establish clearer boundaries between supported application contexts.",
          },
          {
            type: "p",
            text: "Wallet privacy concerns repeated use of the same public address and the ability to associate activity over time. The available Ephemeral Wallet supports separate Solana wallet contexts. Session keys are destroyed on closure unless exported, and public funding and transaction records remain visible.",
          },
          {
            type: "p",
            text: "Transaction privacy concerns the public records created when assets move or programs execute on Solana. Planned mixer and transaction privacy functions are intended to reduce some straightforward relationships, but cannot make public records disappear or promise untraceability.",
          },
          {
            type: "p",
            text: "Network and metadata privacy concerns information observable outside the browser, including IP addresses, connection timing, transaction submission paths, and external service logs. Planned metadata protection and MSG Masking are design concepts whose precise mechanisms and limitations will require implementation specifications.",
          },
          { type: "h2", text: "12.3 Privacy Boundary Matrix" },
          {
            type: "table",
            head: ["Privacy surface", "Zephyr approach", "Important boundary"],
            rows: [
              ["Temporary session data", "Auto Burn Sessions: Available", "Cannot delete copies held by websites or external services"],
              ["Wallet address reuse", "Ephemeral Wallets: Available", "Public funding and transaction relationships may remain visible"],
              ["Application permissions", "Isolation and sandboxing: Coming Soon", "User approvals and information voluntarily shared still matter"],
              ["Transaction linkage", "Mixer: Coming Soon", "Solana transaction records remain public"],
              ["Transaction details and metadata", "MSG Masking and metadata protection: Coming Soon", "No guarantee of concealment from validators or network observers"],
              ["Transfers between chains", "Cross Chain Bridge: Coming Soon", "Bridge operators, contracts, and destination networks have distinct risks"],
            ],
          },
          { type: "p", text: "Table 12.1 Privacy surfaces, intended controls, and boundaries." },
          { type: "h2", text: "12.4 User Control and Informed Authorization" },
          {
            type: "p",
            text: "Zephyr is intended to preserve explicit user authority over sensitive actions. Connecting a wallet, approving an application request, signing a transaction, transferring assets, and publishing content are separate decisions. A convenient browser interface must not treat one approval as unlimited permission for unrelated operations.",
          },
          {
            type: "p",
            text: "When agent functions become available, the same principle applies to autonomous workflows. The AI Agent may prepare steps, present transaction details, or coordinate supported services, but actions involving spending, signatures, deployment, or publication should occur only within permissions the user has deliberately granted. The precise permission model will be documented with the agent implementation.",
          },
          { type: "h2", text: "12.5 Illustrative User Decision Workflow" },
          {
            type: "flow",
            vertical: true,
            caption:
              "Figure 12.1 Conceptual permission and session workflow. The illustration does not imply that every proposed control is currently available.",
            steps: [
              { title: "1. User opens a browsing session" },
              { title: "2. Website or application requests access" },
              { title: "3. Zephyr presents the relevant permission or action" },
              { title: "4. User reviews and authorizes or declines" },
              { title: "5. Approved interaction proceeds within supported controls" },
              { title: "6. Session ends and Auto Burn cleans supported temporary data" },
            ],
          },
          { type: "h2", text: "12.6 Information That May Remain Observable" },
          {
            type: "p",
            text: "A public blockchain retains finalized transaction history according to its network rules. Observers may inspect addresses, token movements, program interactions, and other onchain data. Browser cleanup cannot reverse a completed transfer or erase a transaction from the ledger.",
          },
          {
            type: "p",
            text: "Websites and decentralized applications may retain information they receive, including account identifiers, submitted content, and service logs. Internet providers, network intermediaries, remote procedure call providers, and other infrastructure operators may also observe certain connection details. Zephyr cannot independently control retention practices outside its own environment.",
          },
          {
            type: "p",
            text: "Users may also create linkages through behavior, such as reusing identifiers, moving assets between known addresses, connecting the same social accounts, or disclosing personal information. Privacy depends partly on the choices made during each interaction.",
          },
          { type: "h2", text: "12.7 Data Retention and Session Closure" },
          {
            type: "p",
            text: "Auto Burn runs when Zephyr closes and again when it next starts, including after a crash. It clears cookies, local storage, browsing history, cache, saved logins, service workers, the DNS cache and open tabs. Session cleanup is local and does not erase external records or public blockchain transactions.",
          },
          {
            type: "p",
            text: "A startup cleanup pass clears residual session data left by an interrupted or crashed browser session. This does not prevent websites or external services from retaining information previously received.",
          },
          { type: "h2", text: "12.8 Transparency and Feature Availability" },
          {
            type: "p",
            text: "The browser includes ad free browsing, Auto Burn Sessions and the available Ephemeral Wallet. Transaction privacy modules, encrypted application isolation, metadata protection, MSG Masking, bridge connectivity and the Zephyr AI Agent remain Coming Soon. Availability does not imply anonymity or independent security certification.",
          },
          {
            type: "p",
            text: "Technical descriptions of planned features are intended to communicate architecture and design direction. They do not establish that a particular cryptographic protocol, custody model, anonymity level, security audit, or production guarantee has already been delivered.",
          },
          { type: "h2", text: "12.9 Chapter Summary" },
          {
            type: "p",
            text: "Zephyr treats privacy as a layered and user controlled property of Web3 browsing. Local session cleanup, application boundaries, wallet separation, and transaction privacy address different problems. Their effectiveness must be assessed independently, and the visibility of public Solana records remains a fundamental boundary. The goal is to give users clearer controls and a more understandable privacy model without promising absolute anonymity.",
          },
        ],
      },
    ],
  },
  {
    group: "Ecosystem",
    pages: [
      {
        slug: "zephyr-token",
        chapter: "13",
        title: "The $ZEPHYR Token and Ecosystem Utility",
        blocks: [
          { type: "h2", text: "13.1 Token Overview" },
          {
            type: "p",
            text: "$ZEPHYR is the proposed ecosystem token associated with Zephyr, a privacy focused Web3 browser built around Solana. Its intended role is to support participation in the broader Zephyr environment as token related services and integrations are defined. The browser remains the core product. The token is not presented as a prerequisite for basic browsing or as a guarantee of access to every planned feature.",
          },
          {
            type: "p",
            text: "This chapter describes the current conceptual direction and distinguishes proposed utility from mechanisms that have not been finalized. Specific token contracts, economic parameters, payment flows and distribution arrangements require separate publication and verification before they should be treated as operational specifications.",
          },
          { type: "h2", text: "13.2 Ecosystem Role" },
          {
            type: "p",
            text: "The ecosystem is designed to combine private browsing, optional wallet tools, decentralized application connectivity and intelligent automation. Within that environment, $ZEPHYR may support selected service interactions or ecosystem programs where a token based mechanism provides a clear technical purpose. Any such mechanism would need to be documented with its access rules, costs, custody assumptions and user permissions.",
          },
          {
            type: "p",
            text: "No token holding requirement, staking entitlement, subscription discount, revenue share or governance power is confirmed in this documentation. Those possibilities should not be inferred from the existence of the token.",
          },
          { type: "h2", text: "13.3 Potential Utility Areas" },
          {
            type: "p",
            text: "The following categories identify areas for future consideration rather than active or guaranteed token functions.",
          },
          {
            type: "table",
            head: ["Area", "Conceptual relationship", "Status"],
            rows: [
              ["Browser services", "Optional integration with supported premium or specialized services, if defined.", "Not finalized"],
              ["Privacy infrastructure", "Possible use within selected wallet, routing or privacy modules, subject to technical and regulatory assessment.", "Not finalized"],
              ["AI Agent services", "Possible service payment or access mechanism once the Agent architecture and pricing are specified.", "Not finalized"],
              ["Ecosystem integrations", "Potential utility in supported partner or developer services under published integration rules.", "Not finalized"],
            ],
          },
          { type: "p", text: "Table 13.1. Illustrative utility categories. None represents a committed token feature." },
          { type: "h2", text: "13.4 Relationship to the AI Agent" },
          {
            type: "p",
            text: "The Zephyr AI Agent is envisioned as a browser native system for authorized workflows, project creation, creative production and ongoing project management. The Agent may interact with services that incur operational costs, but no $ZEPHYR fee requirement or automated token payment rule has been confirmed.",
          },
          {
            type: "p",
            text: "The Agent architecture, service fee structure, operational permissions and any related economic model will be described when those mechanisms are finalized. Until then, the token and Agent should be understood as related elements of the proposed ecosystem, not as a completed payment or revenue distribution system.",
          },
          { type: "h2", text: "13.5 Token Lifecycle and Onchain Transparency" },
          {
            type: "p",
            text: "As a Solana ecosystem token, $ZEPHYR activity would be subject to the transparency of the relevant onchain transactions. Token transfers, balances and program interactions may be observable through public network data. Browser session cleanup does not conceal token ownership, reverse completed transfers or remove transaction history.",
          },
          {
            type: "note",
            text: "Users should verify the official token mint address through authenticated project communications before interacting with any asset claiming to represent Zephyr. Similar names and tickers can be created by unrelated parties, so a displayed name alone is not sufficient proof of authenticity.",
          },
          { type: "h2", text: "13.6 Economic Parameters and Distribution" },
          {
            type: "p",
            text: "The final supply specification, circulating supply assumptions, token allocation, treasury arrangements, liquidity provisions, vesting schedules and service fee rules are not established in this chapter. No percentage allocation or fixed economic commitment is asserted.",
          },
          {
            type: "table",
            head: ["Parameter", "Documentation status"],
            rows: [
              ["Token symbol", "$ZEPHYR"],
              ["Primary ecosystem", "Solana"],
              ["Supply and mint configuration", "To be confirmed"],
              ["Allocation and treasury", "To be confirmed"],
              ["Vesting and liquidity", "To be confirmed"],
              ["Fees and revenue distribution", "To be confirmed"],
              ["Governance rights", "Not specified"],
            ],
          },
          { type: "p", text: "Table 13.2. Token parameters requiring final published specifications." },
          { type: "h2", text: "13.7 Conceptual Token Interaction Flow" },
          {
            type: "p",
            text: "The diagram below illustrates a possible permission based service interaction. It is not an implemented transaction route or a promise that a token payment will be required.",
          },
          {
            type: "flow",
            vertical: true,
            caption:
              "Figure 13.1. Conceptual service authorization flow. Actual integration and payment mechanisms remain unconfirmed.",
            steps: [
              { title: "User selects a supported service" },
              { title: "Service presents requirements and any cost" },
              { title: "User reviews and authorizes the action" },
              { title: "Supported wallet or application executes" },
              { title: "Result and onchain record are available" },
            ],
          },
          { type: "h2", text: "13.8 Risk, User Choice and Disclosure" },
          {
            type: "p",
            text: "Digital assets can experience price volatility, limited liquidity, technical failures, malicious impersonation and irreversible transaction loss. Zephyr does not eliminate these risks. Token ownership does not guarantee financial returns, platform revenue, anonymity or future product availability.",
          },
          {
            type: "p",
            text: "Users should independently assess token related risks and review any subsequently published official economic specifications before participating. The token is intended to complement the browser ecosystem, while the browser's privacy and security principles remain distinct from speculative market activity.",
          },
          { type: "h2", text: "13.9 Current Status" },
          {
            type: "p",
            text: "$ZEPHYR is the designated Zephyr ecosystem token. Detailed utility rules, token distribution, treasury policy, economic mechanisms and any relationship to Agent service fees remain subject to final design and publication. This chapter will require revision when verified specifications become available.",
          },
        ],
      },
      {
        slug: "developers",
        chapter: "14",
        title: "Developer Ecosystem and Integrations",
        blocks: [
          { type: "h2", text: "14.1 Purpose and Integration Philosophy" },
          {
            type: "p",
            text: "Zephyr is designed as a privacy focused Web3 browser whose broader capabilities can be extended through carefully controlled integrations. Its developer ecosystem is intended to make decentralized applications, Solana services and future browser modules easier to access without weakening user authorization or exposing more information than an interaction requires.",
          },
          {
            type: "p",
            text: "This chapter describes the intended integration principles and possible extension surfaces. It does not announce a public software development kit, a published application programming interface or an active developer program. Interfaces and access policies will require separate technical specifications before developers can rely on them.",
          },
          { type: "h2", text: "14.2 Integration Surfaces" },
          {
            type: "p",
            text: "Zephyr connects several distinct operating environments. Each environment has its own permissions, data boundaries and failure conditions. Integrations should be designed for the narrowest necessary access rather than broad control over the browser or user wallet.",
          },
          {
            type: "table",
            head: ["Integration surface", "Intended role", "Status"],
            rows: [
              ["Solana applications", "Browser access to supported decentralized applications and wallet requests", "Browser foundation"],
              ["Wallet connectivity", "Explicit account selection, connection approval and transaction authorization", "Supported interactions depend on implementation"],
              ["Privacy modules", "Optional interfaces for Ephemeral Wallets, isolation and transaction privacy", "Available"],
              ["Cross chain services", "Supported asset transfer paths with chain and transaction visibility", "Coming Soon"],
              ["AI Agent workflows", "Permission based browser actions, project creation and connected services", "Coming Soon"],
            ],
          },
          { type: "p", text: "Table 14.1. Conceptual integration surfaces and their current documentation status." },
          { type: "h2", text: "14.3 Solana Application Compatibility" },
          {
            type: "p",
            text: "A Solana application may need to request a wallet connection, present a transaction for review and submit an approved signed transaction to the network. Zephyr aims to support this familiar interaction pattern while maintaining clear separation between application requests and user authorization.",
          },
          {
            type: "p",
            text: "A website requesting a connection should not automatically gain permission to sign transactions. Similarly, a transaction signature should be scoped to the specific action the user has reviewed. Application compatibility must be evaluated against supported wallet interfaces and the behavior of the final browser implementation.",
          },
          { type: "h2", text: "14.4 Developer Interaction Model" },
          {
            type: "p",
            text: "The conceptual workflow below illustrates how an application request should pass through browser controls before an authorized onchain action. It is a design model rather than a published implementation specification.",
          },
          {
            type: "table",
            head: ["Stage", "Interaction", "Required boundary"],
            rows: [
              ["1. Application request", "A supported application asks to connect or perform an action", "Request origin and requested capability remain visible"],
              ["2. Browser mediation", "Zephyr presents the request in its interaction context", "Application cannot silently expand its privileges"],
              ["3. User decision", "User reviews and authorizes or rejects the request", "No signing or spending without appropriate authorization"],
              ["4. Network submission", "An approved signed transaction may be sent to Solana", "Network confirmation and execution are external outcomes"],
              ["5. Session completion", "Supported temporary browser session data is cleaned up", "Public chain records and external service data remain"],
            ],
          },
          { type: "p", text: "Figure 14.1. Conceptual application integration and authorization workflow." },
          { type: "h2", text: "14.5 Privacy Aware Integration Design" },
          {
            type: "p",
            text: "Developers integrating with Zephyr should minimize the collection and persistence of wallet identifiers, browsing metadata and session information. Where the browser offers isolation or temporary identity capabilities, applications should avoid assuming that a user will retain the same address or browser context across sessions.",
          },
          {
            type: "p",
            text: "Application servers, analytics providers and external services may retain their own records. Zephyr cannot automatically delete information held outside its control. Any future privacy interface should document what it protects, what it does not protect and which party is responsible for data handling.",
          },
          { type: "h2", text: "14.6 AI Agent and Connected Services" },
          {
            type: "p",
            text: "The planned Zephyr AI Agent is intended to coordinate browser operations and connected services through user approved workflows. Potential integrations include creative tools, website publishing, social platforms and supported Solana services. Connected account access should be limited to the permissions granted by the user and should remain revocable where the underlying service supports revocation.",
          },
          {
            type: "p",
            text: "The Agent is not intended to treat website content or external service responses as trusted instructions. Sensitive actions such as token deployment, transaction signing, spending and public publishing require explicit operating controls. Detailed connector specifications, agent permissions and service economics are Coming Soon.",
          },
          { type: "h2", text: "14.7 Developer Security Responsibilities" },
          {
            type: "table",
            head: ["Area", "Developer consideration"],
            rows: [
              ["Origin integrity", "Make the requesting application and intended action understandable to the user"],
              ["Permissions", "Request only capabilities needed for the current operation"],
              ["Transaction clarity", "Present amounts, recipients and relevant network context before authorization"],
              ["Data minimization", "Avoid unnecessary storage of addresses, identifiers and session metadata"],
              ["Error handling", "Explain rejected requests, failed submissions and uncertain network outcomes"],
              ["External dependencies", "Disclose reliance on remote services, bridges and other third parties"],
            ],
          },
          { type: "p", text: "Table 14.2. Recommended integration considerations." },
          { type: "h2", text: "14.8 Documentation and Interface Availability" },
          {
            type: "p",
            text: "Future developer materials may define supported connection methods, request and response formats, security requirements, compatibility testing and example integrations. The availability of these materials should not be inferred from this conceptual chapter.",
          },
          {
            type: "p",
            text: "At present, no public integration contract, certification program, developer incentive scheme or application listing policy is established by this documentation. Such details will be published only when they are defined and supported by the product.",
          },
          { type: "h2", text: "14.9 Chapter Summary" },
          {
            type: "p",
            text: "Zephyr aims to support useful Web3 integrations while preserving clear boundaries between applications, browser sessions, wallets and external services. Interoperability should expand user capability without reducing user control.",
          },
        ],
      },
      {
        slug: "vision",
        chapter: "15",
        title: "The Zephyr Vision",
        blocks: [
          { type: "h2", text: "15.1 A Unified Environment for Web3" },
          {
            type: "p",
            text: "Zephyr is designed around a central proposition: privacy, intelligent assistance, and decentralized interaction should be accessible through one coherent browsing environment. As Web3 applications expand, users increasingly manage connections, identities, digital assets, and permissions across multiple interfaces. Zephyr seeks to reduce that fragmentation while preserving clear boundaries between user intent, browser activity, and blockchain execution.",
          },
          {
            type: "p",
            text: "The browser remains the foundation of this vision. Its role is to provide a familiar point of access to websites and decentralized applications while introducing mechanisms that help users control session information and understand the consequences of their actions.",
          },
          { type: "h2", text: "15.2 Privacy as a System of Boundaries" },
          {
            type: "p",
            text: "Zephyr treats privacy as a set of distinct technical concerns rather than a promise of universal anonymity. Temporary browsing information, application permissions, wallet address reuse, transaction visibility, and network metadata each present different risks. Addressing one concern does not automatically resolve the others.",
          },
          {
            type: "p",
            text: "Auto Burn Sessions clear supported temporary session data when Zephyr closes and again at the next start. The available Ephemeral Wallet supports separate wallet contexts. Planned capabilities such as application isolation, metadata protection and transaction privacy would extend these protections into other areas.",
          },
          {
            type: "p",
            text: "Public Solana transactions remain publicly observable. No browser feature can remove immutable onchain records, and privacy protections must be evaluated in the context of these limitations.",
          },
          { type: "h2", text: "15.3 From Browsing to Intelligent Action" },
          {
            type: "p",
            text: "The planned Zephyr AI Agent extends the browser from a place where users navigate applications into an environment where they can describe goals in natural language and coordinate authorized work. Its envisioned scope includes browsing assistance, creative production, project development, website creation, token deployment through StonkFun, and ongoing management of connected project identities.",
          },
          {
            type: "p",
            text: "A project identity is intended to preserve a consistent creative direction across content and platforms, including brand language, visual style, and creator instructions. The agent is designed to support the creator rather than replace their authority. Wallet signatures, spending, deployment, and external publication require suitable user permissions and controls.",
          },
          {
            type: "p",
            text: "Agent architecture, service fees, revenue distribution, and related economic specifications remain Coming Soon. The description here expresses intended capabilities rather than a statement that these systems are currently available.",
          },
          { type: "h2", text: "15.4 The Relationship Between the Core Systems" },
          {
            type: "p",
            text: "The following conceptual model shows how the browser, privacy controls, and planned intelligence capabilities relate to user decisions. It is an architectural illustration, not a finalized implementation diagram.",
          },
          {
            type: "flow",
            vertical: true,
            caption:
              "Figure 15.1. Conceptual relationship between user control, browsing infrastructure, planned privacy modules, intelligent workflows, and external services.",
            steps: [
              { title: "User intent and explicit authorization" },
              { title: "Zephyr browser and session controls" },
              { title: "Privacy modules and application connections" },
              { title: "Optional AI Agent workflows" },
              { title: "Supported Solana and external services" },
            ],
          },
          { type: "h2", text: "15.5 Principles for Ecosystem Development" },
          {
            type: "p",
            text: "The long term direction of Zephyr is guided by three principles. First, privacy controls should be understandable, with their scope and limitations explained rather than overstated. Second, sensitive operations should remain subject to explicit authorization and visible permission boundaries. Third, new capabilities should work as complementary parts of the browser instead of creating additional complexity for users.",
          },
          {
            type: "p",
            text: "For developers, this direction points toward integrations that respect wallet permissions, application boundaries, and user expectations. For creators, it points toward tools that can connect an idea to a digital presence and authorized onchain activity without requiring every step to be managed through separate services.",
          },
          { type: "h2", text: "15.6 What the Vision Does Not Promise" },
          {
            type: "p",
            text: "Zephyr does not claim to make public blockchain activity invisible, guarantee that transactions cannot be linked, eliminate every browser or network threat, or independently verify the safety of every external application. Likewise, planned agent capabilities do not imply unrestricted autonomous access to user funds or accounts.",
          },
          {
            type: "p",
            text: "Future technical specifications will distinguish implemented protections from conceptual designs and identify the assumptions, supported environments, and operational limits relevant to each capability.",
          },
          { type: "h2", text: "15.7 Closing Perspective" },
          {
            type: "p",
            text: "Zephyr aims to make Web3 interaction more deliberate, private, and accessible by combining a browser foundation with complementary privacy infrastructure and intelligent assistance. The objective is not to conceal the nature of decentralized systems, but to give users better tools to navigate them with awareness and control.",
          },
          {
            type: "note",
            text: "Privacy. Security. Control. These principles define the direction of the Zephyr ecosystem.",
          },
        ],
      },
    ],
  },
];

// Flat list in reading order, each page tagged with its group.
export const docsPages = docsSections.flatMap((section) =>
  section.pages.map((page) => ({ ...page, group: section.group }))
);

export const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
